<template>
  <div v-if="themeDone" class="team">
    <!-- Заголовок -->
    <TeamHeader
      :is-my-team="isMyTeam"
      :is-team-step-required="isTeamStepRequired"
      :show-register-additional-step="showRegisterAdditionalStep"
      :logo="logo"
      @skip="onSkip"
    />

    <!-- Меню для моей команды -->
    <div v-if="isMyTeam && !isMobileApp" class="divider"></div>
    <div v-if="isMyTeam && !isMobileApp" class="team__menu">
      <Menu />
      <div class="divider"></div>
    </div>
    <div v-if="!isMyTeam && !isMobileApp" class="divider"></div>

    <!-- Загрузка -->
    <div v-if="!loadingDone" class="team__loading">
      <Loader size="24" />
    </div>
    <!-- ======================================== -->
    <!-- БЛОК 1: Нет команд (пустое состояние) -->
    <!-- ======================================== -->
    <TeamEmpty
      v-if="!isTeamSet && items.length === 0 && loadingDone && isMyTeam && !hasTeamGroups"
      :team-work-rafiki="teamWorkRafiki"
      @create="onTeamCreation"
    />

    <!-- ======================================== -->
    <!-- БЛОК 2: Все команды с поиском и пагинацией -->
    <!-- ======================================== -->
    <div v-if="(!isMyTeam && !isTeamSet && loadingDone) || (isMyTeam && !isTeamSet && loadingDone && !(items.length === 0 && !hasTeamGroups))" class="team__teams-container">
      <div class="team__teams-wrapper">
        <div class="team__info" :class="{ 'team__info_auth': !isMyTeam && isTeamStepRequired }">
          <div class="info__text">
            <div v-if="!isMyTeam && isTeamStepRequired" class="info__step">
              <span>Шаг {{ totalSteps }}</span> из {{ totalSteps }}
            </div>
            <div class="info__title">
              Присоединяйтесь<br>к командам
            </div>
          </div>
          <div
            class="info__image"
            :style="`background-image: url('${teamSpiritRafiki}');`"
          ></div>
        </div>
      </div>
      <div
        v-if="hasTeamGroups && groupsTabs.length > 1"
        class="team__teams-tabs"
        :class="{ 'is-scrollable': isTabsScrollable, 'is-centered': isTabsCentered }"
      >
        <Tabs :items="groupsTabs" :activeItem="activeGroupIndex" @changed="onChangeGroupTab" />
      </div>
      <div class="team__teams-block">
        <div v-if="showTeamsSearch" class="team__teams-search-container">
          <div class="team__teams-search" @click.stop="onShowSearch">
            <div v-if="$root.isMobile" class="teams-search__text">{{ search ? search : 'Начать поиск команды' }}</div>
            <div v-if="$root.isMobile" class="teams-search__icon">
              <Icon name="search" size="s" />
            </div>
            <div v-if="!$root.isMobile" class="teams-search__field">
              <div class="teams-search__label">Название</div>
              <div class="teams-search__input">
                <Input
                  v-model.trim="search"
                  placeholder="Как называется команда?"
                  id="team-search"
                  noBorder
                  :disabled="loading"
                  @keydown="onSearchKeydown"
                ></Input>
              </div>
            </div>
            <div v-if="!$root.isMobile" class="teams-search__action">
              <Button
                variant="primary"
                size="lg"
                text="Найти"
                iconLeft="search"
                @click="onSearch"
                :disabled="loading"
                :loading="loading"
              ></Button>
            </div>
          </div>
        </div>
        <div :class="{ 'team__info_auth': !isMyTeam && isTeamStepRequired }">
          <div class="team__teams">
            <TeamCard
              v-for="(item, i) in items"
              :key="i"
              :name="item.name"
              :image="item.logo || ''"
              :location="item.location"
              :members="item.members"
              class="teams__card"
              @join="onShowConfirm(item)"
            ></TeamCard>
            <div v-if="items.length === 0 && !hasTeamGroups" class="team__teams-empty">
              Ничего не найдено.
            </div>
            <div v-if="items.length === 0 && hasTeamGroups" class="team__teams-empty">
              В этой группе пока нет команд.
            </div>
            <div v-if="totalPages > 1" class="team__pagination">
              <Pagination
                v-if="totalPages > 1"
                :currentPage="currentPage"
                :totalPages="totalPages"
                class=""
                @prev-page="onTeamsChangePage(currentPage - 1)"
                @next-page="onTeamsChangePage(currentPage + 1)"
                @go-page="onTeamsChangePage"
              ></Pagination>
            </div>
          </div>
        </div>
      </div>

      <!-- Баннер "Создайте команду" прибитый к низу - только когда нет групп команд -->
      <div class="team__request">
        <div class="team__request-block">
          <Icon name="empty-flag" />
          <div>
            <div class="team__request-heading">
              Создайте свою команду единомышленников<br>и участвуйте вместе
            </div>
          </div>
        </div>
        <Button
          variant="primary"
          size="lg"
          text="Создать команду"
          @click="onTeamCreation"
        ></Button>
      </div>

    </div>

    <!-- ======================================== -->
    <!-- БЛОК 3: Команды пользователя (с группами) - только когда есть группы -->
    <!-- ======================================== -->
    <div v-if="loadingDone && hasParticipantTeams && hasTeamGroups && !showTeamDetails && isMyTeam" class="team-teams-groups">
      <div class="team__teams">
          <TeamCard
            v-for="(t, i) in participantTeamsList"
            :key="i"
            :name="t.name"
            :image="t.logo || ''"
            :location="t.location"
            :members="t.members"
            class="teams__card"
            isParticipant
            @click.native="onTeamCardClick(t)"
         ></TeamCard>
       </div>
    </div>
  <!-- Баннер "Посмотреть команды" - прибит к низу без отступов -->
    <div v-if="loadingDone && hasParticipantTeams && hasTeamGroups && !showTeamDetails && isMyTeam" class="team__request team__request--no-margins">
      <div class="team__request-block">
        <Icon name="empty-flag" />
        <div>
          <div class="team__request-heading">
            Присоединяйтесь к командам
          </div>
          <p v-if="groupsTabs.length > 1" class="team__request-subheading">Можно вступить в команду ещё в {{ availableGroupsCount }} {{ getGroupCountText(availableGroupsCount) }}</p>
        </div>
      </div>
      <Button
        variant="secondary"
        size="lg"
        text="Посмотреть команды"
        @click="onOpenTeamsModal"
      ></Button>
        </div>

    <!-- ======================================== -->
    <!-- БЛОК 4: Подробности команды пользователя -->
    <!-- ======================================== -->
    <div v-if="showTeamDetails && selectedTeamDetails">
      <div class="team__info team__info_my">
        <div v-if="hasTeamGroups || cameFromModal" class="team__back-button">
          <Button
            variant="secondary"
            :size="$root.isMobile ? 'sm' : 'lg'"
            shape="circle"
            iconLeft="left"
            mobile
            @click="onBackToTeams"
          ></Button>
        </div>
        <div class="info__content">
          <div v-if="selectedTeamDetails.logo" class="info__logo">
            <img :src="selectedTeamDetails.logo" :alt="selectedTeamDetails.name" />
          </div>
          <div class="info__title text-center">
            {{ selectedTeamDetails.name }}
          </div>
          <div class="info__status">
            <div class="status__item status__item_color">
              <Icon name="check-bold" size="m" />
              <span>Вы участник</span>
            </div>
            <div class="status__item">
              <Icon name="user-groups" size="m" />
              <span>{{ membersTotalItems }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showTeamDetails && selectedTeamDetails" class="team__tabs">
      <div class="divider"></div>
      <div class="tabs__tabs">
        <Tabs :items="showTeamRatingsTab ? visibleTabsNaminationsHide : visibleTabs" :activeItem="activeTabIndex" @changed="onChangeTypePage" />
      </div>
    </div>
    <div v-if="showTeamDetails && selectedTeamDetails && selectedType === 0" class="team__members">
      <div class="members__content text-left">
        <div v-if="membersItems.length === 0" class="members__loading">
          <Loader size="24" />
        </div>
        <div v-else>
          <div class="members__entries">
            Показаны<br v-if="$root.isMobile" />
            {{ showPagination ? membersOffset + 1 : 1 }}–{{ showPagination ? Math.min(membersOffset + 10, membersTotalItems) : showItemsCount }} из {{ membersTotalItems }} записей
          </div>
        <div class="members__table">
          <table>
            <tr
              v-for="(item, i) in membersItems"
              :key="i"
              class="members__rating-item"
              :class="{ 'rating-item--active': item.current === true }"
            >
              <td class="rating-item__number" v-if="item.change === 'up'">{{ item.number }}<span class="number--up">&#8239;&#8593;</span></td>
              <td class="rating-item__number" v-else-if="item.change === 'down'">{{ item.number }}<span class="number--down">&#8239;&#8595;</span></td>
              <td class="rating-item__number" v-else>{{ item.number }}</td>
              <td class="rating-item__name">{{ item.name }}</td>
              <td class="rating-item__value">
                <div>
                  <span>{{ item.value }}</span>
                  <img :src="pointImage" width="18" height="18"/>
                </div>
              </td>
            </tr>
          </table>
        </div>
        <Button
          v-if="membersCurrentPage === 1 && membersItems.length < membersTotalItems"
          variant="secondary"
          size="md"
          mobile
          :wide="true"
          class="members__more"
          @click="onMembersMore"
        >
          Показать ещё
        </Button>
        <Pagination
          v-if="showPagination && membersItems.length < membersTotalItems"
          :currentPage="membersCurrentPage"
          :totalPages="membersTotalPages"
          class="members__pagination"
          @prev-page="onMembersChangePage(membersCurrentPage - 1)"
          @next-page="onMembersChangePage(membersCurrentPage + 1)"
          @go-page="onMembersChangePage"
        ></Pagination>
        </div>
      </div>
    </div>

    <div v-show="showTeamDetails && selectedTeamDetails && selectedType === 1 && !showTeamRatingsTab" class="team__nominations">
      <div class="nominations__types">
        <Tabs :items="[getTabText(0), getTabText(1)]" :activeItem="selectedTab" @changed="onChangeTab" />
      </div>
      <div class="nominations__items">
        <div v-show="selectedTab === 0">
          <NominationCard
            v-for="(item, i) in itemsTeamActive"
            :key="i"
            :ratingText="teamRatingText"
            :lockedText="lockedText"
            type="personal"
            state="in-progress"
            class="team-rating"
            :nominationText="getNominationDates(item)"
            :title="item.title"
            :values="getNominationRating(item)"
            @show-details="onShowRating(item, true)"
          ></NominationCard>
        </div>
        <div v-show="selectedTab === 1">
          <NominationCard
            v-for="(item, i) in itemsTeamCompleted"
            :key="i"
            :ratingText="teamRatingText"
            :lockedText="lockedText"
            :resultText="resultText"
            type="personal"
            state="result"
            class="team-rating"
            :nominationText="getNominationDates(item)"
            :title="item.title"
            :resultValues="getNominationResult(item)"
            @show-details="onShowRating(item, true)"
          ></NominationCard>
        </div>
        <div class="nominations__loader" v-if="updating">
          <Loader size="24" />
        </div>
        <div class="nominations__loader-block"></div>
      </div>
    </div>

    <div v-if="showTeamDetails && selectedTeamDetails && selectedType === 2" class="team__details">
      <div class="details__container text-left">
        <div class="details__title">О команде</div>
        <div class="details__content">
          <div v-if="teamDescription" class="details__description" v-html="teamDescription"></div>
          <div v-if="teamLocationAndType" class="details__location-type">
            <div class="location-type__title">
              <span>Где находится клуб</span>
              <span></span>
            </div>
            <div class="location-type__text" v-html="teamLocationAndType"></div>
          </div>
          <div v-if="teamLink" class="details__link">
            <div class="link__title">
              <span>Сайт или соц. сеть клуба</span>
              <span></span>
            </div>
            <div class="link__text">
              <Link
                variant="primary"
                size="md"
                iconRight="externa-link"
                tag="a"
                :href="teamLink"
                :text="getLinkWithoutProtocol(teamLink)"
                :title="teamLink"
                target="_blank"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="details__change-team">
        <Icon name="info-circle-s" size="s" />
        <Link
          variant="secondary"
          size="md"
          text="Как сменить команду?"
          @click="onTeamChange"
        />
      </div>
    </div>

    <div v-if="showTeamDetails && selectedTeamDetails && selectedType === 3 && teamAchievements && teamAchievements.length > 0" class="team__details">
      <div class="details__container text-left">
        <div class="details__achievements">
          <div v-for="(item, i) in teamAchievements" :key="i" class="details__achievement" @click="onAchievementModal(item)">
            <img :src="item.achievementIconUrl || ''" :alt="item.title || 'Достижение'">
            <span class="details__achievement-title">{{ item.title || item.key || 'Достижение' }}</span>
            <span class="details__achievement-text">{{ item.description || 'Описание недоступно' }}</span>
            <div class="details__achievement-count"  v-if="item.state !== 'Unlocked'" >
              <Icon name="team" size="m" />
              <span>{{ item.finishedParticipantCount || 0 }}/{{ item.settings && item.settings.UnlockLimit ? item.settings.UnlockLimit : '?' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal
      v-if="achievementModalItem"
      name="team-achievements"
      class="team-achievements"
      :class="{ 'for-mobile-app': isMobileApp }"
      :active="achievementModal"
      @close="achievementModalClose"
    >
      <div class="team-achievements__content">
        <div class="team-achievements__header">
          <div class="team-achievements__img">
            <img :src="achievementModalItem.achievementIconUrl" alt="">
          </div>
          <p class="team-achievements__title">{{ achievementModalItem.title }}</p>
          <p class="team-achievements__subtitle">{{ achievementModalItem.description }}</p>
          <Loader v-if="achievementModalItem.teamAchievementId && (!achievemenParticipants || !achievemenParticipants.participants)"  size="24" />
          <div v-if="achievementModalItem.teamAchievementId && achievemenParticipants && achievemenParticipants.state !== 'Unlocked'" class="team-achievements__participants-count">
            <Icon name="team" size="m" />
            {{ achievemenParticipants.finishedParticipantCount }}/{{ achievemenParticipants.settings.UnlockLimit }}
          </div>
          <div v-if="achievementModalItem.teamAchievementId && achievemenParticipants && achievemenParticipants.participants" class="team-achievements__participants">
            <p class="team-achievements__participants-title">Внесли вклад в достижение</p>
            <ul class="team-achievements__participants-list">
              <li class="team-achievements__participants-item" :class="{'is-active' : achievemenParticipants.participants[i].isCurrent}" v-for="(item, i) in achievemenParticipants.participants" :key="i">
                <span>{{ i + 1 }}.</span>
                <span class="team-achievements__participants-name">{{ item.firstName}} {{ item.lastName }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Modal>

    <Modal
      name="about-teams"
      class="about-teams"
      :class="{ 'for-mobile-app': isMobileApp }"
      :img="teamSpiritRafiki"
      title="Присоединившись к команде:"
      :active="aboutTeamsModal"
      :noTransition="true"
      @close.prevent="onAboutTeamsClosed"
    >
      <div class="about-teams__text text-left">
        Очки, которые вы заработаете за выполнение заданий, будут добавляться в копилку вашей команды.<br><br>
        Вы начинаете принимать участие во внутрикомандном зачете.
      </div>
      <div class="buttons-group">
        <div class="buttons-group__item buttons-group__item_first">
          <Button
            variant="secondary"
            size="lg"
            text="Понятно"
            @click="onAboutTeamsClosed"
            wide
            mobile
          ></Button>
        </div>
      </div>
    </Modal>

    <Modal
      name="team-search"
      class="team-search"
      :closable="true"
      :class="{
        'for-mobile-app': isMobileApp,
        'show-modal-close': isMobileApp,
        'team-search-modal': isMobileApp,
      }"
      title="Поиск команды"
      :active="showSearch"
      :noTransition="true"
      @close.prevent="onSearchClose"
    >
      <div class="team-search__container">
        <div class="team-search__field text-left">
          <div class="field__label">Название</div>
          <div class="field__input">
            <Input
              v-model.trim="search"
              placeholder="Как называется команда?"
              id="team-search"
              noBorder
              mobile
              :disabled="loading"
              @keydown="onSearchKeydown"
            ></Input>
          </div>
        </div>
        <div class="divider"></div>
        <div class="">
          <Button
            variant="primary"
            size="lg"
            text="Найти"
            iconLeft="search"
            @click="onSearch"
            wide
            mobile
            :disabled="loading"
            :loading="loading"
          ></Button>
        </div>
      </div>
    </Modal>

    <Modal
      name="team-confirm"
      class="team-confirm"
      :img="teamLogo"
      :title="teamName"
      :subtitle="teamLocationAndType"
      :active="showConfirm"
      :noTransition="true"
      @close.prevent="onConfirmClose"
    >
      <div class="raffle-info">
        <div class="raffle-info__members">
          <Icon name="user-groups" size="m" /><span>{{ teamMembersCount }} {{ membersCountText }}</span>
        </div>
        <div v-if="teamLink" class="raffle-info__link">
          <Icon name="global" size="m" />
          <Link
            variant="primary"
            size="md"
            iconRight="externa-link"
            tag="a"
            :href="teamLink"
            :text="getLinkWithoutProtocol(teamLink)"
            :title="teamLink"
            target="_blank"
          />
        </div>
      </div>
      <div
        v-if="teamDescription"
        class="team-confirm__description"
        v-html="teamDescription"
      ></div>

      <!-- Предупреждение если нельзя вступить в команду -->
      <Alert
        v-if="!canJoinTeam"
        state="warning"
        noAction
      >
        В этой группе вы уже присоединились к команде
      </Alert>

      <div v-if="canJoinTeam" class="team-confirm__actions">
        <Button
          variant="primary"
          size="lg"
          text="Вступить в команду"
          @click="onTeamConfirmPrimary"
          :wide="$root.isMobile"
          :disabled="confirmModalLoading"
          :loading="confirmModalLoading"
          mobile
        ></Button>
        <div class="team-confirm__or">
          <div class="or__line"></div>
          <div class="or__text">или</div>
          <div class="or__line"></div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          text="Выбрать другую команду"
          @click="onConfirmCloseAndOpenTeams"
          :wide="$root.isMobile"
          :disabled="confirmModalLoading"
          mobile
        ></Button>
      </div>

      <!-- Кнопка "Понятно" если нельзя вступить -->
      <div v-if="!canJoinTeam" class="team-confirm__actions">
        <Button
          variant="secondary"
          size="lg"
          text="Понятно"
          @click="onConfirmCloseAndOpenTeams"
          :wide="$root.isMobile"
          mobile
        ></Button>
      </div>
    </Modal>

    <Modal
      name="group-join-confirm"
      class="group-join-confirm"
      icon="warning"
      :title="`Подтвердите выбор команды <span style='color: var(--color-primary, #FF8233); font-weight: 600;'>«${teamName}»</span>`"
      :active="showGroupConfirm"
      :noTransition="true"
      @close.prevent="onGroupConfirmClose"
    >
      <div class="text-center" style="padding: 0 12px;">
        Можно выбрать только одну команду в каждой категории. Выйти из команды возможно только через запрос в поддержку
      </div>
      <div class="buttons-group">
        <Button
          variant="primary"
          size="lg"
          text="Подтвердить"
          @click="confirmGroupJoin"
          :disabled="loading || !canJoinTeam"
          :loading="loading"
          mobile
          :wide="$root.isMobile"
        ></Button>
        <Button
          variant="secondary"
          size="lg"
          text="Отменить"
          @click="onGroupConfirmClose"
          mobile
          :wide="$root.isMobile"
        ></Button>
      </div>
    </Modal>

    <!-- Модалка подтверждения для вступления в команду (без групп) -->
    <Modal
      name="team-join-confirm"
      class="team-join-confirm"
      icon="warning"
      :title="`Подтвердите выбор команды <span style='color: var(--color-primary, #FF8233); font-weight: 600;'>«${teamName}»</span>`"
      :active="showTeamConfirm"
      :noTransition="true"
      @close.prevent="onTeamConfirmClose"
    >
      <div class="buttons-group">
        <Button
          variant="primary"
          size="lg"
          text="Подтвердить"
          @click="confirmTeamJoin"
          :disabled="loading || !canJoinTeam"
          :loading="loading"
          mobile
          :wide="$root.isMobile"
        ></Button>
        <Button
          variant="secondary"
          size="lg"
          text="Отменить"
          @click="onTeamConfirmClose"
          mobile
          :wide="$root.isMobile"
        ></Button>
      </div>
    </Modal>

    <Modal
      name="team-change"
      class="team-change"
      :class="{ 'for-mobile-app': isMobileApp }"
      icon="info-circle-s"
      title="Для смены команды:"
      :active="teamChangeModal"
      :noTransition="true"
      @close.prevent="onTeamChangeClosed"
    >
      <p class="team-change__subtitle">напишите, пожалуйста, письмо в <a href="#" @click.prevent="onSupport">Поддержку</a> с названием команды, в которую хотите попасть</p>
    </Modal>

    <SupportDialog
      :active="supportModal"
      :isMobileApp="isMobileApp"
      @close="onSupportClose"
      @form-submitted="handleFormSubmit"
    />

    <Modal
      :closable="true"
      name="successModal"
      :active="successModal"
      :class="{ 'for-mobile-app': isMobileApp }"
      @close="onSuccessClose"
    >
      <div class="modal__success">
        <Icon name="check-circle"></Icon>
        <p class="modal__success-title">Вопрос отправлен</p>
        <p class="modal__success-text">Обычно отвечаем в течение трёх рабочих дней. Ответ придёт на почту <span>{{ email }}</span> </p>
        <div class="moadal__success-action">
          <Button
            variant="secondary"
            size="lg"
            tag="a"
            mobile
            @click="onSuccessClose"
          >
            Закрыть
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      name="team-creation-warning"
      class="team-creation-warning"
      :class="{ 'for-mobile-app': isMobileApp }"
      :title="teamCreateModalTitle"
      :active="showTeamCreationModal"
      :noTransition="true"
      @close.prevent="onTeamCreationClosed"
    >
      <div class="creation-warning__info">
        <div class="creation-warning__text text-left">
          <ul type="disc">
            <li v-for="(item, index) in teamCreateModalInfo" :key="index">{{ item.text }}</li>
            <li v-if="!teamCreateModalInfo || teamCreateModalInfo.length === 0">Направление: ЗОЖ или спорт (например, беговой клуб);</li>
            <li v-if="!teamCreateModalInfo || teamCreateModalInfo.length === 0">Команда (сообщество) должна состоять из двух и более участников;</li>
          </ul>
        </div>
        <div v-if="teamCreateModalSubtext" class="creation-warning__subtext">
          Если все соответствует, то смело отправляйте заявку на регистрацию команды.
        </div>
      </div>
      <div class="buttons-group">
        <div class="buttons-group__item">
          <Button
            v-if="!isMobileApp"
            variant="primary"
            size="lg"
            text="Продолжить"
            tag="a"
            :href="teamCreateLink"
            target="_blank'"
            wide
            mobile
          ></Button>
          <Button
            v-if="isMobileApp"
            variant="primary"
            size="lg"
            text="Продолжить"
            @click="onTeamCreationCreate"
            wide
            mobile
          ></Button>
        </div>
        <div class="buttons-group__item buttons-group__item_first">
          <Button
            variant="secondary"
            size="lg"
            text="Отменить"
            @click="onTeamCreationClosed"
            wide
            mobile
          ></Button>
        </div>
      </div>
    </Modal>

    <Modal
      name="creation-finished"
      state="success"
      class="creation-finished"
      :class="{ 'for-mobile-app': isMobileApp }"
      :title="teamCreateFinishModalTitle"
      :subtitle="teamCreateFinishModalSubtitle"
      :active="finishedModal"
      :noTransition="true"
      @close.prevent="onFinishedClosed"
    >
      <div class="buttons-group">
        <div class="buttons-group__item buttons-group__item_first">
          <Button
            variant="secondary"
            size="lg"
            text="Понятно"
            @click="onCreationFinished"
            mobile
          ></Button>
        </div>
      </div>
    </Modal>

    <Modal
      :active="teamOffLimits"
      name="TeamOffLimitsModal"
      title="Ваша команда уже собрана"
      :subtitle="teamOffLimitsSubtitle"
      icon="info-circle"
      @close.prevent="onTeamOffLimitsClose"
    >
      <Button
        variant="secondary"
        size="lg"
        mobile
        @click="onTeamOffLimitsClose"
      >
        Понятно
      </Button>
    </Modal>

    <RulesPrizesModal
      :active="rulesPrizesModal"
      :image="rulesPrizesImage"
      :title="rulesPrizesTitle"
      :subtitle="rulesPrizesSubtitle"
      :nominationId="rulesPrizesNominationId"
      :isTeam="rulesPrizesIsTeam"
      state="personal"
      :tabs="rulesPrizesTabs"
      :activeTab="rulesPrizesTabActive"
      @close="rulesPrizesModal = false"
    />

    <TeamJoinedModal
      :active="previewConfirmModal"
      @close="onCloseTeamsModal"
      @join="onShowConfirm"
      @show-team-info="onTeamCardClick"
      @show-team-details="onShowTeamDetails"

    />

    <!-- Alert для ошибки вступления в команду -->
    <Alert
      v-if="showTeamJoinError"
      state="warning"
      class="team-page-error"
    >
      {{ teamJoinErrorMessage }}
      <template #action>
        <Button
          variant="secondary"
          size="sm"
          text="Закрыть"
          @click="closeTeamJoinError"
        />
      </template>
    </Alert>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import TeamCard from '@sc-component-library/team-card/src/main';
import Input from '@rr-component-library/input/src/main';
import Loader from '@rr-component-library/loader/src/main';
import Alert from '@rr-component-library/alert/src/main';
import NominationCard from '@sc-component-library/nomination-card/src/main';
import { v4 as uuidv4 } from 'uuid';
import Pagination from '@/components/Pagination/Pagination.vue';
import Menu from '@/components/Menu/Menu.vue';
import RulesPrizesModal from '@/components/RulesPrizesModal/RulesPrizesModal.vue';
import Point from '@/assets/img/point.svg';
import SupportDialog from '@/components/SupportDialog/SupportDialog.vue';
import { formatDateRange } from '@/assets/js/helpers';
import TeamJoinedModal from '@/components/TeamJoinedModal/TeamJoinedModal.vue';

// Новые подкомпоненты
import TeamHeader from '@/components/Team/TeamHeader.vue';
import TeamEmpty from '@/components/Team/TeamEmpty/TeamEmpty.vue';

export default {
  name: 'Team',
  props: {
    firstName: String,
  },
  components: {
    Input,
    Loader,
    Alert,
    NominationCard,
    TeamCard,
    Pagination,
    Menu,
    RulesPrizesModal,
    SupportDialog,
    TeamJoinedModal,
    // Новые подкомпоненты
    TeamHeader,
    TeamEmpty,
  },
  data() {
    return {
      // Пагинация и поиск
      items: [],
      currentPage: 1,
      itemsPerPage: 6,
      totalPages: 0,
      search: '',
      showSearch: false,
      loading: false,
      hasUsedSearch: false, // Флаг использования поиска

      // Данные команды
      teamName: '',
      teamId: '',
      teamLogo: '',
      teamLocationAndType: '',
      teamLink: '',
      teamDescription: '',
      teamMembersCount: 0,

      // Модалки
      showConfirm: false,
      showGroupConfirm: false,
      showTeamConfirm: false, // Модалка подтверждения для команды без групп
      teamOffLimits: false,
      aboutTeamsModal: false,
      teamChangeModal: false,
      showTeamCreationModal: false,
      finishedModal: false,
      supportModal: false,
      successModal: false,

      // Табы и навигация
      selectedType: 0,
      selectedTab: 0,
      tabs: [
        { title: 'Участники', enabled: true },
        { title: 'Номинации', enabled: true },
        { title: 'Обзор', enabled: true },
      ],
      tabsNaminationsHide: [
        { title: 'Участники', enabled: true },
        { title: 'Обзор', enabled: true },
      ],

      // Тексты для номинаций
      teamRatingText: { general: 'Текущий рейтинг', personal: 'Команда в рейтинге' },
      lockedText: { text: 'Для участия необходимо', link: 'подключить трекер' },
      resultText: { rating: 'Итоговый результат', reward: 'Ваша награда' },

      // Участники команды
      membersCurrentPage: 1,
      membersTotalPages: 1,
      membersTotalItems: 0,
      membersItems: [],
      showItemsCount: 10,
      membersOffset: 0,
      showPagination: true,
      pointImage: Point,

      // Правила и призы
      rulesPrizesModal: false,
      rulesPrizesTitle: '',
      rulesPrizesSubtitle: '',
      rulesPrizesNominationId: '',
      rulesPrizesIsTeam: false,
      rulesPrizesImage: '',
      rulesPrizesTabs: [
        { title: 'Правила', enabled: true, content: '' },
        { title: 'Награды', enabled: true, content: '' },
        { title: 'Рейтинг', enabled: true },
      ],
      rulesPrizesTabActive: 0,
      itemsTeamActive: [],
      itemsTeamCompleted: [],
      offsetTeamActive: 0,
      offsetTeamCompleted: 0,
      updating: false,
      loaderBlock: null,
      loadingDone: false,
      achievementModal: false,
      showTeamDetails: false,
      selectedTeamDetails: null,
      achievementModalItem: null,
      achievemenParticipants: null,
      previewConfirmModal: false,
      itemsTeamGroups: [],
      activeGroupIndex: 0,
      cameFromModal: false,
      canJoinTeam: true,
      confirmModalLoading: false,
      email: '',
      // Состояние для восстановления после перезагрузки
      savedState: null,

      // Состояние для управления табами с прокруткой
      isTabsScrollable: false,
      isTabsCentered: false,

      // Состояние для отображения ошибок
      showTeamJoinError: false,
      teamJoinErrorMessage: '',
    };
  },
  computed: {
    ...mapGetters('settings', ['theme', 'themeDone', 'options', 'resources']),
    ...mapGetters('user', ['profile', 'isAuthenticated', 'emptyProfile', 'questionnaireStatus', 'isMobileApp']),
    ...mapGetters('teams', ['teams', 'team', 'teamGroups', 'participantTeams']),
    ...mapGetters('nominations', ['teamNominationsActive', 'teamNominationsCompleted', 'teamMembersResults', 'ratings', 'responseStatus']),
    ...mapGetters('achievements', ['teamAchievements', 'teamAchievementParticipants']),

    isMyTeam() {
      return this.$route.name === 'MyTeam';
    },
    isTeamSet() {
      return this.profile ? this.profile.teams !== undefined && this.profile.teams.length > 0 : false;
    },
    isTeamCreated() {
      return this.$route.query.isTeamCreated === 'true';
    },
    myTeamId() {
      return this.profile && this.profile.teams && this.profile.teams.length > 0 ? this.profile.teams[0].teamId : null;
    },
    myTeamName() {
      return this.profile && this.profile.teams && this.profile.teams.length > 0 ? this.profile.teams[0].name : '';
    },
    myTeamLogo() {
      return this.profile && this.profile.teams && this.profile.teams.length > 0 ? this.profile.teams[0].logo : '';
    },
    isTeamsEnabled() {
      return this.options ? this.options.isTeamsEnabled === true : false;
    },
    isTeamStepRequired() {
      return this.options ? this.options.isTeamStepRequired === true : false;
    },
    showTeamCreate() {
      return this.options ? this.options.showTeamCreate === true : false;
    },
    showTeamRatingsTab() {
      return this.options ? this.options.showTeamRatingsTab === true : false;
    },
    teamCreateModalInfo() {
      return this.options ? this.options.teamCreateModalInfo || [] : [];
    },
    teamCreateModalTitle() {
      return this.options ? this.options.teamCreateModalTitle || 'Требования для регистрации новой команды' : '';
    },
    teamCreateModalSubtext() {
      return this.options && this.options.teamCreateModalSubtext === true;
    },
    teamCreateFinishModalTitle() {
      return this.options ? this.options.teamCreateFinishModalTitle || 'Заявка на регистрацию команды принята!' : '';
    },
    teamCreateFinishModalSubtitle() {
      return this.options ? this.options.teamCreateFinishModalSubtitle || 'Решение о создании команды придёт вам на email, указанный в заявке' : '';
    },
    showRegisterAdditionalStep() {
      return this.options ? this.options.showRegisterAdditionalStep === true : false;
    },
    disableTeamsForGuest() {
      return this.options ? this.options.disableTeamsForGuest === true : false;
    },
    logo() {
      return this.resources ? this.resources.logo || '' : '';
    },
    teamSpiritRafiki() {
      return this.resources ? this.resources.teamSpiritRafiki || '' : '';
    },
    teamWorkRafiki() {
      return this.resources ? this.resources.teamWorkRafiki || '' : '';
    },
    articleTeams() {
      return this.resources ? this.resources.articleTeams || '#' : '#';
    },
    supportEmail() {
      return this.resources ? this.resources.supportEmail || '#' : '#';
    },
    totalSteps() {
      return this.showRegisterAdditionalStep ? 3 : 2;
    },
    showOffset() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    teamCreateLink() {
      return this.isMyTeam ? '/my-team/create' : '/team/create';
    },
    teamOffLimitsSubtitle() {
      return `На данный момент в вашей команде нет свободных мест для принятия участия. Свяжитесь с организатором Программы в вашей компании или напишите нам в техническую поддержку<br/>
      <a href="mailto:${this.supportEmail}">${this.supportEmail}</a>`;
    },
    activeTabIndex() {
      if (this.showTeamRatingsTab) {
        const tabMap = { 0: 0, 3: 1, 2: 2 };
        return tabMap[this.selectedType] || 0;
      }

      // Порядок табов: Участники (0), Номинации (1), Достижения (3), Обзор (2)
      const tabMap = {
        0: 0,
        1: 1,
        3: 2,
        2: 3,
      };
      return tabMap[this.selectedType] || 0;
    },
    visibleTabs() {
      const baseTabs = [
        { title: 'Участники', enabled: true },
        { title: 'Номинации', enabled: true },
      ];
      if (this.teamAchievements && this.teamAchievements.length > 0) {
        baseTabs.push({ title: 'Достижения', enabled: true });
      }
      baseTabs.push({ title: 'Обзор', enabled: true });
      return baseTabs;
    },
    visibleTabsNaminationsHide() {
      const baseTabs = [
        { title: 'Участники', enabled: true },
      ];
      if (this.teamAchievements && this.teamAchievements.length > 0) {
        baseTabs.push({ title: 'Достижения', enabled: true });
      }
      baseTabs.push({ title: 'Обзор', enabled: true });
      return baseTabs;
    },

    hasTeamGroups() {
      const groups = Array.isArray(this.itemsTeamGroups) ? this.itemsTeamGroups : [];
      // Проверяем только наличие групп, независимо от наличия команд в текущей группе
      return groups.length > 0;
    },
    groupsTabs() {
      const groups = Array.isArray(this.itemsTeamGroups) ? this.itemsTeamGroups : [];
      return groups.map((g) => ({
        title: g.name || g.title || g.key || 'Группа',
        enabled: true,
        icon: g.participantJoined ? 'check-bold' : null,
      }));
    },
    showTeamsSearch() {
      // Если поиск уже использовался, показываем его всегда
      if (this.hasUsedSearch) return true;

      // Для групп команд показываем поиск только если команд больше 6
      if (this.hasTeamGroups) {
        if (!this.teams) return false;
        return typeof this.teams.total === 'number' ? this.teams.total > 6 : false;
      }

      // Для обычного списка команд показываем поиск только если команд больше 6
      if (!this.teams) return false;
      return typeof this.teams.total === 'number' ? this.teams.total > 6 : false;
    },
    hasParticipantTeams() {
      const list = this.participantTeamsList;
      return Array.isArray(list) && list.length > 0;
    },
    participantTeamsList() {
      if (!this.participantTeams) return [];
      // API may return { values: [...] } or array
      if (Array.isArray(this.participantTeams)) return this.participantTeams;
      return Array.isArray(this.participantTeams.values) ? this.participantTeams.values : [];
    },
    availableGroupsCount() {
      if (!Array.isArray(this.itemsTeamGroups)) return 0;
      return this.itemsTeamGroups.filter((group) => !group.participantJoined).length;
    },
    membersCountText() {
      const count = this.teamMembersCount;
      if (count === 1) return 'участник';
      if (count >= 2 && count <= 4) return 'участника';
      return 'участников';
    },
  },
  methods: {
    ...mapActions('user', ['getProfile', 'applyAnswers']),
    ...mapActions('wallet', ['getBalance']),
    ...mapActions('teams', ['getTeams', 'getTeamsByGroup', 'getTeamById', 'getTeamGroups', 'joinTeam', 'getParticipantTeams']),
    ...mapActions('nominations', ['getTeamNominationsActive', 'getTeamNominationsCompleted', 'getTeamMembersResults', 'getRatings']),
    ...mapActions('exercises', ['sendSupport']),
    ...mapActions('achievements', ['getTeamAchievements', 'getTeamAchievementParticipants']),

    // ========================================
    // МЕТОДЫ ДЛЯ РАБОТЫ С КОМАНДАМИ
    // ========================================

    onTeamCardClick(teamOrData) {
      // Проверяем, пришел ли объект с дополнительными данными
      const team = teamOrData.team || teamOrData;
      const fromModal = teamOrData.fromModal || false;

      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      this.selectedTeamDetails = team;
      this.showTeamDetails = true;
      this.selectedType = 0; // Начинаем с таба "Участники"
      this.cameFromModal = fromModal;

      this.saveState();

      // Загружаем данные команды
      if (team.teamId) {
        this.getTeamById({ teamId: team.teamId })
          .then(() => {
            if (this.team) {
              this.teamName = this.team.name || team.name || '';
              this.teamId = team.teamId || '';
              this.teamLogo = this.team.logo || team.logo || '';
              this.teamMembersCount = this.team.members || 0;
              this.setTeamValues(this.team);
            }
          });

        // Загружаем участников команды
        this.getTeamMembersResults({ teamId: team.teamId, offset: 0, count: 10 })
          .then(() => {
            if (this.teamMembersResults) {
              this.membersItems = this.getRating(this.teamMembersResults.values);
              this.membersTotalPages = Math.ceil(this.teamMembersResults.total / 10);
              this.membersTotalItems = this.teamMembersResults.total;
            }
          });

        // Загружаем номинации команды
        this.loadNominations();

        // Загружаем достижения команды
        this.getTeamAchievements(team.teamId);
      }
    },
    onOpenTeamsModal() {
      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      // Загружаем группы команд перед открытием модалки
      this.getTeamGroups({ searchString: this.search, offset: 0, count: 100 })
        .then(() => {
          const tg = this.teamGroups;
          let groups = [];
          if (tg && Array.isArray(tg.values)) {
            groups = tg.values;
          } else if (Array.isArray(tg)) {
            groups = tg;
          }
          this.itemsTeamGroups = groups;

          // Загружаем команды в группе
          if (groups.length > 0) {
            return this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage });
          }
          return this.getTeams({ searchString: this.search, offset: 0, count: this.itemsPerPage });
        })
        .then(() => {
          this.previewConfirmModal = true;
          this.saveState();
          // Проверяем переполнение табов
          this.$nextTick(this.checkTabsOverflow);
        })
        .catch(() => {
          this.previewConfirmModal = true;
          this.saveState();
        });
    },
    onShowTeamDetails({ team, canJoin }) {
      // Закрываем модалку команд
      this.previewConfirmModal = false;

      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      // Сразу устанавливаем состояние кнопок
      this.canJoinTeam = canJoin;
      this.teamName = team.name || '';
      this.teamId = team.teamId || '';
      this.teamLogo = team.logo || '';
      this.teamLocationAndType = '';
      this.teamLink = '';
      this.teamDescription = '';

      // Показываем модалку подтверждения
      this.showConfirm = true;
      this.confirmModalLoading = true;
      this.saveState();

      // Загружаем полные данные команды асинхронно
      this.getTeamById({ teamId: team.teamId })
        .then(() => {
          if (this.team) {
            this.teamName = this.team.name || team.name || '';
            this.teamId = team.teamId || '';
            this.teamLogo = this.team.logo || team.logo || '';
            this.teamMembersCount = this.team.members || 0;
            this.setTeamValues(this.team);
            this.saveState();
          }
        })
        .finally(() => {
          this.confirmModalLoading = false;
        });
    },

    // ========================================
    // МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ СОСТОЯНИЕМ
    // ========================================

    saveState() {
      const state = {
        showTeamDetails: this.showTeamDetails,
        selectedTeamDetails: this.selectedTeamDetails,
        cameFromModal: this.cameFromModal,
        previewConfirmModal: this.previewConfirmModal,
        showConfirm: this.showConfirm,
        showGroupConfirm: this.showGroupConfirm,
        showTeamConfirm: this.showTeamConfirm,
        canJoinTeam: this.canJoinTeam,
        confirmModalLoading: this.confirmModalLoading,
        selectedType: this.selectedType,
        timestamp: Date.now(),
      };
      localStorage.setItem('teamPageState', JSON.stringify(state));
    },

    restoreState() {
      try {
        const saved = localStorage.getItem('teamPageState');
        if (saved) {
          const state = JSON.parse(saved);
          // Проверяем, что состояние не старше 30 минут или timestamp в будущем (проблема с временем)
          const timeDiff = Math.abs(Date.now() - state.timestamp);
          if (timeDiff < 30 * 60 * 1000) {
            // Если у пользователя нет команд, не восстанавливаем состояние команды
            if (this.isTeamSet) {
              // Не перезаписываем showTeamDetails если оно уже установлено в true
              if (!this.showTeamDetails) {
                this.showTeamDetails = state.showTeamDetails || false;
              }
              // Не перезаписываем selectedTeamDetails если оно уже установлено для команды пользователя
              if (!this.selectedTeamDetails || this.selectedTeamDetails.teamId !== this.myTeamId) {
                this.selectedTeamDetails = state.selectedTeamDetails || null;
              }
            } else {
              // Если у пользователя нет команд, сбрасываем состояние команды
              this.showTeamDetails = false;
              this.selectedTeamDetails = null;
              // Очищаем сохраненное состояние, так как пользователя убрали из команды
              localStorage.removeItem('teamPageState');
            }
            this.cameFromModal = state.cameFromModal || false;

            // Не восстанавливаем showConfirm если есть группы команд, чтобы избежать автоматического открытия групповой модалки
            if (!this.hasTeamGroups) {
              this.showConfirm = state.showConfirm || false;
            } else {
              this.showConfirm = false;
            }

            this.showGroupConfirm = state.showGroupConfirm || false;
            this.showTeamConfirm = state.showTeamConfirm || false;
            this.canJoinTeam = state.canJoinTeam !== undefined ? state.canJoinTeam : true;
            this.confirmModalLoading = state.confirmModalLoading || false;
            this.selectedType = state.selectedType || 0;

            // Сбрасываем ошибку при восстановлении состояния
            this.showTeamJoinError = false;
            this.teamJoinErrorMessage = '';

            // Восстанавливаем модалку команд если она была открыта
            if (state.previewConfirmModal) {
              this.previewConfirmModal = true;
            }

            // Если восстанавливаем состояние команды, загружаем данные
            if (this.selectedTeamDetails && this.selectedTeamDetails.teamId) {
              this.loadTeamData(this.selectedTeamDetails.teamId);
            }

            // Проверяем переполнение табов после восстановления состояния
            this.$nextTick(this.checkTabsOverflow);
          } else {
            localStorage.removeItem('teamPageState');
          }
        }
      } catch (error) {
        console.error('Error restoring state:', error);
        localStorage.removeItem('teamPageState');
      }
    },

    loadTeamData(teamId) {
      // Загружаем данные команды
      this.getTeamById({ teamId })
        .then(() => {
          if (this.team) {
            this.teamName = this.team.name || '';
            this.teamId = teamId;
            this.teamLogo = this.team.logo || '';
            this.teamMembersCount = this.team.members || 0;
            this.setTeamValues(this.team);
          }
        });

      // Загружаем участников команды
      this.getTeamMembersResults({ teamId, offset: 0, count: 10 })
        .then(() => {
          if (this.teamMembersResults) {
            this.membersItems = this.getRating(this.teamMembersResults.values);
            this.membersTotalPages = Math.ceil(this.teamMembersResults.total / 10);
            this.membersTotalItems = this.teamMembersResults.total;
          }
        });

      // Загружаем номинации и достижения
      this.loadNominations();
      this.getTeamAchievements(teamId);
    },

    onCloseTeamsModal() {
      this.previewConfirmModal = false;
      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
      this.saveState();
    },

    onBackToTeams() {
      this.showTeamDetails = false;
      this.selectedTeamDetails = null;

      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      // Если мы пришли из модалки, возвращаемся к ней
      if (this.cameFromModal) {
        this.previewConfirmModal = true;
        this.cameFromModal = false;
      } else if (this.hasTeamGroups) {
        // Если есть группы команд, возвращаемся к списку команд пользователя
        // Ничего не делаем, просто скрываем детали
      } else {
        // Если нет групп команд, пользователь должен остаться на странице команды
        // Не показываем список команд для выбора
      }

      this.saveState();
    },

    getCurrentTeamGroupId() {
      const groups = this.itemsTeamGroups || [];
      const group = groups[this.activeGroupIndex] || groups[0];
      if (!group) return null;
      return group.teamGroupId || group.id || group.groupId || group.key || group.code || null;
    },
    fetchTeamsByCurrentGroup({ offset, count }) {
      const teamGroupId = this.getCurrentTeamGroupId();
      if (teamGroupId) {
        return this.getTeamsByGroup({
          teamGroupId, searchString: this.search, offset, count,
        })
          .then(() => {
            this.items = this.teams.values;
            this.totalPages = Math.ceil(this.teams.total / this.itemsPerPage);
          });
      }
      // Fallback if no groups available
      return this.getTeams({ searchString: this.search, offset, count })
        .then(() => {
          this.items = this.teams.values;
          this.totalPages = Math.ceil(this.teams.total / this.itemsPerPage);
        });
    },
    onAchievementModal(item) {
      this.achievementModalItem = item;
      this.achievementModal = true;
      if (!item.teamAchievementId) {
        this.achievemenParticipants = null;
        return;
      }
      this.achievemenParticipants = null;
      this.getTeamAchievementParticipants(item.teamAchievementId)
        .then(() => {
          this.achievemenParticipants = this.teamAchievementParticipants;
        })
        .catch(() => {
          this.achievemenParticipants = null;
        });
    },
    prefillPreview(item) {
      if (!item) return;
      this.getTeamById({ teamId: item.teamId })
        .then(() => {
          if (this.team) {
            this.teamName = this.team.name || item.name || '';
            this.teamId = item.teamId || '';
            this.teamLogo = this.team.logo || item.logo || '';
            this.teamMembersCount = this.team.members || 0;
            this.setTeamValues(this.team);
          } else {
            this.teamName = item.name || '';
            this.teamId = item.teamId || '';
            this.teamLogo = item.logo || '';
            this.teamLocationAndType = '';
            this.teamLink = '';
            this.teamDescription = '';
          }
        });
    },
    achievementModalClose() {
      this.achievementModal = false;
      this.achievementModalItem = null;
      this.achievemenParticipants = null;
    },
    onSupport() {
      this.teamChangeModal = false;
      this.$root.$setLocationHash('#!showSupport=true');
    },
    onSupportClose() {
      this.$root.$setLocationHash('#!showSupport=false');
    },
    onSuccessClose() {
      this.$root.$setLocationHash('#!showSupportSuccess=false');
    },
    handleFormSubmit(formData) {
      this.supportModal = false;
      this.$root.$setLocationHash('#!showSupport=false');
      this.sendSupport(formData)
        .then(() => {
          this.email = formData.email;
          this.$root.$setLocationHash('#!showSupportSuccess=true');
        });
    },

    // ========================================
    // МЕТОДЫ ЗАГРУЗКИ ДАННЫХ
    // ========================================

    checkProfile() {
      this.getProfile()
        .then(() => {
          if (this.isAuthenticated) {
            // Если у пользователя нет команд, сбрасываем состояние команды
            if (!this.isTeamSet) {
              this.showTeamDetails = false;
              this.selectedTeamDetails = null;
              localStorage.removeItem('teamPageState');
            }
            if (this.emptyProfile === true) {
              this.$router.push({ name: 'Register' }).catch(() => {});
              return;
            }
            this.getBalance();
            if (this.isTeamPageDisabled() && !this.isMobileApp) {
              this.$router.push({ name: 'Dashboard' }).catch(() => {});
              return;
            }
            if (this.isTeamSet) {
              this.getTeamAchievements(this.myTeamId);
              if (!this.isMyTeam) {
                this.$router.push({ name: 'Dashboard' }).catch(() => {});
                return;
              }

              // Если у пользователя есть команда и нет групп, сразу показываем подробности
              this.getTeamGroups({ searchString: this.search, offset: 0, count: 100 })
                .then(() => {
                  const tg = this.teamGroups;
                  let groups = [];
                  if (tg && Array.isArray(tg.values)) {
                    groups = tg.values;
                  } else if (Array.isArray(tg)) {
                    groups = tg;
                  }
                  this.itemsTeamGroups = groups;

                  if (!this.hasTeamGroups) {
                    // Нет групп - показываем подробности команды пользователя
                    this.selectedTeamDetails = {
                      teamId: this.myTeamId,
                      name: this.myTeamName,
                      logo: this.myTeamLogo,
                    };
                    this.showTeamDetails = true;
                    this.selectedType = 0;
                    this.loadingDone = true;

                    // Загружаем полные данные команды асинхронно
                    this.loadTeamData(this.myTeamId);

                    // Восстанавливаем состояние
                    this.restoreState();
                    return;
                  }

                  // Есть группы - загружаем команды пользователя
                  this.getParticipantTeams()
                    .then(() => {
                      this.getTeamMembersResults({ teamId: this.myTeamId, offset: 0, count: 10 })
                        .then(() => {
                          if (this.teamMembersResults) {
                            this.membersItems = this.getRating(this.teamMembersResults.values);
                            this.membersTotalPages = Math.ceil(this.teamMembersResults.total / 10);
                            this.membersTotalPages = Math.ceil(this.teamMembersResults.total / 10);
                            this.membersTotalItems = this.teamMembersResults.total;
                          }

                          this.getTeamById({ teamId: this.myTeamId })
                            .then(() => {
                              if (this.team) {
                                this.teamName = this.team.name || '';
                                this.teamId = this.myTeamId || '';
                                this.teamLogo = this.team.logo || '';
                                this.setTeamValues(this.team);
                              }
                              this.loadingDone = true;
                              // Восстанавливаем состояние после загрузки всех данных
                              this.restoreState();
                              // Проверяем переполнение табов
                              this.$nextTick(this.checkTabsOverflow);
                            });
                        });
                    });
                })
                .catch(() => {
                  this.itemsTeamGroups = [];
                  // Если ошибка загрузки групп, показываем подробности команды
                  this.selectedTeamDetails = {
                    teamId: this.myTeamId,
                    name: this.myTeamName,
                    logo: this.myTeamLogo,
                  };
                  this.showTeamDetails = true;
                  this.selectedType = 0;
                  this.loadingDone = true;

                  // Загружаем полные данные команды асинхронно
                  this.loadTeamData(this.myTeamId);

                  // Восстанавливаем состояние
                  this.restoreState();
                });
              return;
            }

            if (this.isTeamCreated) {
              this.onFinished();
            }

            this.loading = true;
            this.fetchTeamGroups()
              .then(() => {
                // Если нет групп, команды уже загружены в fetchTeamGroups
                // Если есть группы, загружаем команды по группе
                if (this.hasTeamGroups) {
                  return this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage });
                }
                return Promise.resolve();
              })
              .then(() => {
                this.getRatings({ filter: null, aggregateField: 'TeamId' })
                  .then(() => {
                    if (this.previewConfirmModal && this.items && this.items.length > 0 && !this.teamId) {
                      this.prefillPreview(this.items[0]);
                    }
                    setTimeout(() => {
                      this.loading = false;
                      this.loadingDone = true;
                      this.restoreState();
                      this.$nextTick(this.checkTabsOverflow);
                    }, 300);
                  });
              });
          } else {
            this.$router.push({ name: 'Auth' }).catch(() => {});
          }
        });
    },
    onHashChange() {
      if (this.$route.name !== 'Team' && this.$route.name !== 'MyTeam') {
        window.removeEventListener('hashchange', this.onHashChange);
        return;
      }
      const hash = window.location.hash.split('=');
      if (hash[0] === '#!showAboutTeams') this.showAboutTeams(hash);
      else if (hash[0] === '#!showTeamChange') this.showTeamChange(hash);
      else if (hash[0] === '#!showTeamCreation') this.showTeamCreation(hash);
      else if (hash[0] === '#!showTeamCreationFinished') this.showTeamCreationFinished(hash);
      else if (hash[0] === '#!showSupport') this.showSupport(hash);
      else if (hash[0] === '#!showSupportSuccess') this.showSupportSuccess(hash);
      else {
        this.clearHash();
        this.aboutTeamsModal = false;
        this.teamChangeModal = false;
        this.showTeamCreationModal = false;
        this.finishedModal = false;
        this.supportModal = false;
        this.successModal = false;
      }
    },
    onAboutTeams() {
      if (this.isMobileApp && !this.loadingDone) {
        return;
      }
      this.$root.$setLocationHash('#!showAboutTeams=true');
    },
    onAboutTeamsClosed() {
      this.$root.$setLocationHash('#!showAboutTeams=false');
    },
    onTeamChange() {
      this.$root.$setLocationHash('#!showTeamChange=true');
    },
    onTeamChangeClosed() {
      this.$root.$setLocationHash('#!showTeamChange=false');
    },
    onTeamCreation() {
      window.location.hash = '#!showTeamCreation=true';
    },
    onTeamCreationCreate() {
      this.onTeamCreationClosed();
      setTimeout(() => { this.$root.$goPage('TeamCreate', `${this.teamCreateLink}?title=Команда`); }, 250);
    },
    onTeamCreationClosed() {
      this.$root.$setLocationHash('#!showTeamCreation=false');
    },
    onTeamsChangePage(page) {
      this.currentPage = page;
      this.loading = true;

      // Сбрасываем ошибку при смене страницы
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      this.fetchTeamsByCurrentGroup({ offset: this.showOffset, count: this.itemsPerPage })
        .then(() => {
          this.loading = false;
          // Проверяем переполнение табов после смены страницы
          this.$nextTick(this.checkTabsOverflow);
        });
    },
    onShowConfirm(item) {
      if (item) {
        // Сбрасываем ошибку при показе нового подтверждения
        this.showTeamJoinError = false;
        this.teamJoinErrorMessage = '';

        this.getTeamById({ teamId: item.teamId })
          .then(() => {
            if (this.team) {
              this.teamName = this.team.name || '';
              this.teamId = item.teamId || '';
              this.teamLogo = this.team.logo || '';
              this.teamMembersCount = this.team.members || 0;
              this.setTeamValues(this.team);
              if (this.teamName && this.teamId) {
                this.showConfirm = true;
              }
            }
          });
      }
    },
    onGroupConfirmClose() {
      this.showGroupConfirm = false;
      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
      // Открываем модалку выбора команд
      this.previewConfirmModal = true;
      this.saveState();
    },
    onTeamConfirmClose() {
      this.showTeamConfirm = false;
      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
      // Открываем модалку выбора команд
      this.previewConfirmModal = true;
      this.saveState();
    },
    confirmGroupJoin() {
      // Не закрываем модалку сразу, оставляем её открытой с лоадером
      this.onSelectTeam();
    },
    confirmTeamJoin() {
      // Не закрываем модалку сразу, оставляем её открытой с лоадером
      this.onSelectTeam();
    },
    onConfirmClose() {
      this.showConfirm = false;
      this.confirmModalLoading = false;
      // Сбрасываем ошибку при закрытии модального окна
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
    },
    onConfirmCloseAndOpenTeams() {
      this.showConfirm = false;
      this.confirmModalLoading = false;
      // Сбрасываем ошибку
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
      // Открываем модалку выбора команд
      this.previewConfirmModal = true;
      this.saveState();
    },
    onShowSearch() {
      if (this.$root.isMobile) {
        // Сбрасываем ошибку при показе поиска
        this.showTeamJoinError = false;
        this.teamJoinErrorMessage = '';

        this.showSearch = true;
        setTimeout(() => {
          const item = document.getElementById('team-search');
          if (item) item.focus();
        }, 100);
      }
    },
    onSearchKeydown(event) {
      if (event.key === 'Enter') {
        this.onSearch();
      }
    },
    onSearch() {
      this.showSearch = false;
      this.loading = true;
      this.hasUsedSearch = true; // Отмечаем что поиск использовался

      // Сбрасываем ошибку при поиске
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage })
        .then(() => {
          this.currentPage = 1;
          this.loading = false;
          // Проверяем переполнение табов после поиска
          this.$nextTick(this.checkTabsOverflow);
        });
    },
    onSearchClose() {
      this.showSearch = false;
      // Сбрасываем ошибку при закрытии поиска
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
    },
    onSelectTeam() {
      // New flow: POST /team-participants with generated UUID
      const payload = { teamParticipantId: uuidv4(), teamId: this.teamId };

      // Запускаем лоадер в модалке
      this.loading = true;
      this.confirmModalLoading = true;

      this.joinTeam(payload)
        .then((response) => {
          const status = response && response.status;
          if (status === 200 || status === 204 || status === 201) {
            // Очищаем сохраненное состояние
            localStorage.removeItem('teamPageState');

            // Сразу закрываем модалки и сбрасываем лоадеры
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.showTeamConfirm = false;
            this.previewConfirmModal = false;
            this.loading = false;
            this.confirmModalLoading = false;

            // Небольшая задержка для плавности
            setTimeout(() => {
              if (this.isMyTeam) {
                // Для десктопа обновляем данные без перезагрузки
                if (!this.isMobileApp) {
                  this.getProfile().then(() => {
                    // Обновляем данные команды
                    if (this.profile && this.profile.teams && this.profile.teams.length > 0) {
                      this.getParticipantTeams().then(() => {
                        // Если нет групп команд, автоматически показываем подробности команды
                        if (!this.hasTeamGroups && this.participantTeamsList.length > 0) {
                          this.onTeamCardClick(this.participantTeamsList[0]);
                        }
                      });
                    }
                    // Обновляем группы команд
                    this.fetchTeamGroups().then(() => {
                      // Проверяем переполнение табов после обновления групп
                      this.$nextTick(this.checkTabsOverflow);
                    });
                  });
                } else {
                  // Для WebView просто обновляем данные без дополнительной навигации
                  this.getProfile().then(() => {
                    // Обновляем данные команды
                    if (this.profile && this.profile.teams && this.profile.teams.length > 0) {
                      this.getParticipantTeams().then(() => {
                        // Если нет групп команд, автоматически показываем подробности команды
                        if (!this.hasTeamGroups && this.participantTeamsList.length > 0) {
                          this.onTeamCardClick(this.participantTeamsList[0]);
                        }
                      });
                    }
                    // Обновляем группы команд
                    this.fetchTeamGroups().then(() => {
                      // Проверяем переполнение табов после обновления групп
                      this.$nextTick(this.checkTabsOverflow);
                    });
                  });
                }
              } else if (this.isMobileApp && this.isTeamsEnabled && this.isTeamStepRequired && !this.showRegisterAdditionalStep) {
                this.$router.push({ name: 'RegisterComplete' }).catch(() => {});
              } else {
                window.location.href = `${window.location.origin}/dashboard?isFirstLogin=true`;
              }
            }, 300);
          } else if (status === 409) {
            // Закрываем модалки при ошибке
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.showTeamConfirm = false;
            this.previewConfirmModal = false;
            this.teamOffLimits = true;
            this.loading = false;
            this.confirmModalLoading = false;
          } else if (status === 422) {
            // Обрабатываем ошибку "участник уже в другой команде в этой группе"
            // Закрываем модалку вступления в команду
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.showTeamConfirm = false;
            this.loading = false;
            this.confirmModalLoading = false;

            // Показываем ошибку в модалке выбора команд
            this.showTeamJoinError = true;
            this.teamJoinErrorMessage = 'В этой категории вы уже присоединились к команде';

            // Модалка выбора команд уже открыта, просто показываем ошибку
            this.saveState();

            // Автоматически скрываем Alert через 5 секунд
            setTimeout(() => {
              this.showTeamJoinError = false;
              this.teamJoinErrorMessage = '';
            }, 5000);
          } else if (status === 409) {
            // Обрабатываем ошибку "команда уже заполнена"
            // Закрываем модалку вступления в команду
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.loading = false;
            this.confirmModalLoading = false;

            // Показываем ошибку о том, что команда заполнена
            this.showTeamJoinError = true;
            this.teamJoinErrorMessage = 'Команда уже заполнена, нет свободных мест';

            // Модалка выбора команд уже открыта, просто показываем ошибку
            this.saveState();

            // Автоматически скрываем Alert через 5 секунд
            setTimeout(() => {
              this.showTeamJoinError = false;
              this.teamJoinErrorMessage = '';
            }, 5000);
          } else {
            // Закрываем модалки при других ошибках
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.previewConfirmModal = false;
            this.loading = false;
            this.confirmModalLoading = false;
          }
        })
        .catch((error) => {
          // Обрабатываем ошибку 422 из catch блока
          if (error && error.response && error.response.status === 422) {
            // Закрываем модалку вступления в команду
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.loading = false;
            this.confirmModalLoading = false;

            // Показываем ошибку в модалке выбора команд
            this.showTeamJoinError = true;
            this.teamJoinErrorMessage = 'В этой категории вы уже присоединились к команде';

            // Модалка выбора команд уже открыта, просто показываем ошибку
            this.saveState();

            // Автоматически скрываем Alert через 5 секунд
            setTimeout(() => {
              this.showTeamJoinError = false;
              this.teamJoinErrorMessage = '';
            }, 5000);
          } else if (error && error.response && error.response.status === 409) {
            // Обрабатываем ошибку "команда уже заполнена"
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.loading = false;
            this.confirmModalLoading = false;

            // Показываем ошибку о том, что команда заполнена
            this.showTeamJoinError = true;
            this.teamJoinErrorMessage = 'Команда уже заполнена, нет свободных мест';

            // Модалка выбора команд уже открыта, просто показываем ошибку
            this.saveState();

            // Автоматически скрываем ошибку через 5 секунд
            setTimeout(() => {
              this.showTeamJoinError = false;
              this.teamJoinErrorMessage = '';
            }, 5000);
          } else {
            // Закрываем модалки при других исключениях
            this.showConfirm = false;
            this.showGroupConfirm = false;
            this.previewConfirmModal = false;
            this.loading = false;
            this.confirmModalLoading = false;
          }
        });
    },

    // ========================================
    // МЕТОДЫ НАВИГАЦИИ И ДЕЙСТВИЙ
    // ========================================

    onSkip() {
      this.$router.push({ name: 'Dashboard', query: { isFirstLogin: 'true' } }).catch(() => {});
    },
    onNominationsLinkClick() {
      if (this.isMobileApp && this.$root.$goPage) {
        this.$root.$goPage('NominationsInProgress', '/rating/in-progress&title=Рейтинг');
      } else {
        this.$router.push({ name: 'NominationsInProgress', query: this.isMobileApp ? { addToHistory: 'true' } : {} }).catch(() => {});
      }
    },
    onChangeTypePage(index) {
      // Используем teamId из selectedTeamDetails, если он есть, иначе myTeamId
      const teamId = this.selectedTeamDetails ? this.selectedTeamDetails.teamId : this.myTeamId;

      if (this.showTeamRatingsTab) {
        if (index === 1) {
          this.getTeamAchievements(teamId);
          this.selectedType = 3;
          this.saveState();
          return;
        }
        if (index === 2) {
          this.selectedType = 2;
          this.saveState();
          return;
        }
        this.selectedType = 0;
        this.saveState();
      } else {
        if (index === 1) {
          this.loadNominations();
          this.saveState();
          return;
        }
        if (index === 2) {
          this.getTeamAchievements(teamId);
          this.selectedType = 3;
          this.saveState();
          return;
        }
        if (index === 3) {
          this.selectedType = 2;
          this.saveState();
          return;
        }
        this.selectedType = index;
        this.saveState();
      }
    },
    onChangeTab(index) {
      this.selectedTab = index;
    },
    onMembersChangePage(page) {
      window.scrollTo(0, 0);
      this.membersCurrentPage = page;
      this.loadRatingPage();
    },
    onMembersMore() {
      if (this.membersTotalItems === this.membersItems.length) return;
      let showItemsCount = this.showItemsCount + 10;
      if (showItemsCount > this.membersTotalItems) showItemsCount = this.membersTotalItems;
      this.showItemsCount = showItemsCount;
      const offset = this.membersOffset + 10;
      if (offset > this.membersTotalItems) return;
      this.membersOffset = offset;
      this.showPagination = false;
      // Используем teamId из selectedTeamDetails, если он есть, иначе myTeamId
      const teamId = this.selectedTeamDetails ? this.selectedTeamDetails.teamId : this.myTeamId;
      this.getTeamMembersResults({ teamId, offset: this.membersOffset, count: 10 })
        .then(() => {
          if (this.teamMembersResults) {
            this.membersItems.push(...this.getRating(this.teamMembersResults.values));
          }
        });
    },
    onShowRating(item, isTeam = false) {
      this.rulesPrizesTabs[0].content = item.rules || '';
      this.rulesPrizesTabs[1].content = item.awards || '';
      this.rulesPrizesTabs[2].enabled = true;
      this.rulesPrizesTabActive = 2;
      this.rulesPrizesImage = '';
      this.rulesPrizesTitle = this.getNominationDates(item);
      this.rulesPrizesSubtitle = item.title;
      this.rulesPrizesNominationId = item.nominationId;
      this.rulesPrizesIsTeam = isTeam;
      this.rulesPrizesModal = true;
    },
    onFinished() {
      this.$root.$setLocationHash('#!showTeamCreationFinished=true');
    },
    onFinishedClosed() {
      this.$root.$setLocationHash('#!showTeamCreationFinished=false');
      setTimeout(() => { window.location.reload(); }, 200);
    },
    onCreationFinished() {
      if (this.isMobileApp) {
        this.finishedModal = false;
        return;
      }
      this.$root.$setLocationHash('#!showTeamCreationFinished=false');
      setTimeout(() => {
        this.$router.replace({ query: '' });
        window.location.reload();
      }, 500);
    },
    setupLoader() {
      const vm = this;
      if (vm.loaderBlock !== null) return;
      if (vm.$route.name !== 'MyTeam') {
        return;
      }
      const loaders = document.getElementsByClassName('nominations__loader-block');
      vm.loaderBlock = loaders[0] || null;
      window.addEventListener('scroll', vm.checkForLoading);
      window.addEventListener('touchmove', vm.checkForLoading);
      vm.checkForLoading();
    },
    checkForLoading() {
      if (this.updating) return;
      if (!this.loaderBlock) return;
      if (this.$route.name !== 'MyTeam') {
        this.cleanup();
        return;
      }

      if (this.getNominations() === null) return;
      if (!this.hasNominationsForLoading()) return;

      const bottomCoord = this.loaderBlock.getBoundingClientRect().bottom;
      const height = document.documentElement.clientHeight;
      if (height >= bottomCoord) {
        this.updating = true;
        setTimeout(() => { this.updateTeamNominations(); }, 250);
      }
    },
    cleanup() {
      window.removeEventListener('scroll', this.checkForLoading);
      window.removeEventListener('touchmove', this.checkForLoading);
    },
    loadNominations() {
      const vm = this;
      this.getTeamNominationsActive({ offset: 0, count: 12 })
        .then(() => {
          this.itemsTeamActive = this.teamNominationsActive ? this.teamNominationsActive.values : [];
          this.selectedType = 1;
          setTimeout(() => {
            vm.setupLoader();
          }, 250);
        });
      this.getTeamNominationsCompleted({ offset: 0, count: 12 })
        .then(() => {
          this.itemsTeamCompleted = this.teamNominationsCompleted ? this.teamNominationsCompleted.values : [];
          if ((!this.teamNominationsActive || this.teamNominationsActive.total === 0)
            && this.teamNominationsCompleted && this.teamNominationsCompleted.total > 0) {
            this.selectedTab = 1;
          }
        });
    },
    updateTeamNominations() {
      if (this.selectedTab === 0) {
        this.offsetTeamActive += 12;
        this.getTeamNominationsActive({ offset: this.offsetTeamActive, count: 12 })
          .then(() => {
            if (this.responseStatus === 200) {
              this.itemsTeamActive.push(...this.teamNominationsActive.values);
            }
            this.updating = false;
          });
        return;
      }
      if (this.selectedTab === 1) {
        this.offsetTeamCompleted += 12;
        this.getTeamNominationsCompleted({ offset: this.offsetTeamCompleted, count: 12 })
          .then(() => {
            if (this.responseStatus === 200) {
              this.itemsTeamCompleted.push(...this.teamNominationsCompleted.values);
            }
            this.updating = false;
          });
      }
    },
    getTabText(tab) {
      if (tab === 0) return { title: `<div>${this.teamNominationsActive ? this.teamNominationsActive.total : 0}</div><div>Активны</div>`, enabled: this.teamNominationsActive ? this.teamNominationsActive.total > 0 : false };
      if (tab === 1) return { title: `<div>${this.teamNominationsCompleted ? this.teamNominationsCompleted.total : 0}</div><div>Завершены</div>`, enabled: this.teamNominationsCompleted ? this.teamNominationsCompleted.total > 0 : false };
      return { title: '', enabled: false };
    },
    getNominations() {
      if (this.selectedTab === 0) return this.teamNominationsActive;
      if (this.selectedTab === 1) return this.teamNominationsCompleted;
      return null;
    },
    hasNominationsForLoading() {
      let nominations = { count: 0 };
      let items = [];
      if (this.selectedTab === 0) {
        nominations = this.teamNominationsActive;
        items = this.itemsTeamActive;
      }
      if (this.selectedTab === 1) {
        nominations = this.teamNominationsCompleted;
        items = this.itemsTeamCompleted;
      }
      if (nominations.count === 0) return false;
      if (items.length >= nominations.total) return false;
      return true;
    },
    getGroupCountText(count) {
      if (count === 0 || count >= 2) return 'категориях';
      return 'категории';
    },
    loadRatingPage() {
      this.membersOffset = (this.membersCurrentPage - 1) * 10;
      // Используем teamId из selectedTeamDetails, если он есть, иначе myTeamId
      const teamId = this.selectedTeamDetails ? this.selectedTeamDetails.teamId : this.myTeamId;
      this.getTeamMembersResults({ teamId, offset: this.membersOffset, count: 10 })
        .then(() => {
          if (this.teamMembersResults) {
            this.membersItems = this.getRating(this.teamMembersResults.values);
            this.membersTotalPages = Math.ceil(this.teamMembersResults.total / 10);
            this.membersTotalItems = this.teamMembersResults.total;
          }
        });
    },
    getRating(values) {
      if (!values) return [];
      const rating = [];
      for (let i = 0; i < values.length; i += 1) {
        const it = {
          number: values[i].position.toString(),
          name: `${values[i].lastName} ${values[i].firstName}`,
          value: values[i].points.toString(),
          current: values[i].isCurrentUser === true,
        };
        rating.push(it);
      }
      return rating;
    },
    getNominationDates(item) {
      if (!item) return '';
      const startDate = dayjs.utc(item.startDate);
      const finishDate = dayjs.utc(item.finishDate);
      if (startDate.isValid() && finishDate.isValid()) {
        return formatDateRange({ start: startDate, finish: finishDate, showYear: true });
      }
      return '';
    },
    getNominationRating(item) {
      if (!item) return [];
      const rating = [];
      for (let i = 0; i < item.leaders.length; i += 1) {
        const it = {
          number: item.leaders[i].position.toString(),
          name: item.leaders[i].teamName,
          value: item.leaders[i].points.toString(),
          current: item.leaders[i].isCurrentTeam === true,
        };
        rating.push(it);
      }
      return rating;
    },
    getNominationResult(item) {
      const emptyResult = {
        rating: { number: '', name: '', value: '' },
      };
      if (!item) return emptyResult;
      const result = item.leaders.filter((i) => i.isCurrentTeam === true);
      if (result.length === 0) return emptyResult;
      return {
        rating: { number: result[0].position.toString(), name: result[0].teamName, value: result[0].points.toString() },
      };
    },

    // ========================================
    // УТИЛИТАРНЫЕ МЕТОДЫ
    // ========================================

    getTeamMembersCount(teamId) {
      if (!this.ratings) return 0;
      const found = this.ratings.filter((i) => i.id === teamId);
      if (found.length === 0) return 0;
      return found[0].count || 0;
    },
    isTeamPageDisabled() {
      return !this.isTeamSet && this.isMyTeam && this.disableTeamsForGuest;
    },
    getLinkWithoutProtocol(link) {
      if (!link) return '';
      const https = 'https://';
      if (link.indexOf(https) === 0) return link.slice(https.length);
      const http = 'http://';
      if (link.indexOf(http) === 0) return link.slice(http.length);
      return link;
    },
    setTeamValues(item) {
      if (!item) return;
      const type = item.onlineClub === true ? 'Онлайн клуб' : 'Офлайн клуб';
      if (item.location) {
        this.teamLocationAndType = `${item.location} • ${type}`;
      } else {
        this.teamLocationAndType = `${type}`;
      }
      this.teamLink = item.url || '';
      this.teamDescription = item.description || '';
    },

    // ========================================
    // МЕТОДЫ УПРАВЛЕНИЯ МОДАЛКАМИ
    // ========================================

    showAboutTeams(hash) {
      if (hash[1] === 'true') this.aboutTeamsModal = true;
      else {
        this.aboutTeamsModal = false;
        this.clearHash();
      }
    },
    showTeamChange(hash) {
      if (hash[1] === 'true') this.teamChangeModal = true;
      else {
        this.teamChangeModal = false;
        this.clearHash();
      }
    },
    showTeamCreation(hash) {
      if (hash[1] === 'true') this.showTeamCreationModal = true;
      else {
        this.showTeamCreationModal = false;
        this.clearHash();
      }
    },
    showTeamCreationFinished(hash) {
      if (hash[1] === 'true') this.finishedModal = true;
      else {
        this.finishedModal = false;
        this.clearHash();
      }
    },
    showSupport(hash) {
      if (hash[1] === 'true') this.supportModal = true;
      else {
        this.supportModal = false;
        this.clearHash();
      }
    },
    showSupportSuccess(hash) {
      if (hash[1] === 'true') this.successModal = true;
      else {
        this.successModal = false;
        this.clearHash();
      }
    },
    clearHash() {
      setTimeout(() => { this.$root.$setLocationHash(''); }, 100);
    },
    onTeamOffLimitsClose() {
      this.teamOffLimits = false;
    },
    fetchTeamGroups() {
      return this.getTeamGroups({ searchString: this.search, offset: 0, count: 100 })
        .then(() => {
          const tg = this.teamGroups;
          let groups = [];
          if (tg && Array.isArray(tg.values)) {
            groups = tg.values;
          } else if (Array.isArray(tg)) {
            groups = tg;
          }
          this.itemsTeamGroups = groups;

          // If there are groups, load by group; otherwise load all teams
          if (!this.isTeamSet && this.items.length === 0) {
            if (groups.length > 0) {
              return this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage });
            }
            return this.getTeams({ searchString: this.search, offset: 0, count: this.itemsPerPage })
              .then(() => {
                this.items = this.teams.values || [];
                this.totalPages = Math.ceil((this.teams.total || 0) / this.itemsPerPage);
              });
          }

          // Проверяем переполнение табов после загрузки групп
          this.$nextTick(this.checkTabsOverflow);
          return Promise.resolve();
        })
        .catch(() => {
          this.itemsTeamGroups = [];
          return Promise.resolve();
        });
    },
    onChangeGroupTab(index) {
      this.activeGroupIndex = index;
      this.currentPage = 1;
      this.loading = true;

      // Сбрасываем ошибку при смене группы
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';

      this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage })
        .then(() => {
          this.loading = false;
          this.$nextTick(this.checkTabsOverflow);
        });
    },
    onTeamConfirmPrimary() {
      if (this.hasTeamGroups) {
        this.showConfirm = false;
        this.showGroupConfirm = true;
        return;
      }
      // Для команд без групп показываем модалку подтверждения
      this.showConfirm = false;
      this.showTeamConfirm = true;
    },

    // ========================================
    // МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ТАБАМИ С ПРОКРУТКОЙ
    // ========================================

    checkTabsOverflow() {
      if (!this.hasTeamGroups || this.groupsTabs.length <= 1) {
        this.isTabsScrollable = false;
        this.isTabsCentered = false;
        return;
      }

      const tabsContainer = document.querySelector('.team__teams-tabs .tabs__content');
      if (!tabsContainer) {
        this.isTabsScrollable = false;
        this.isTabsCentered = false;
        return;
      }

      const isScrollable = tabsContainer.scrollWidth > tabsContainer.clientWidth;
      this.isTabsScrollable = isScrollable;

      // Центрируем табы только если они не прокручиваются
      this.isTabsCentered = !isScrollable;
    },

    // Метод для закрытия Alert с ошибкой
    closeTeamJoinError() {
      this.showTeamJoinError = false;
      this.teamJoinErrorMessage = '';
    },
  },
  mounted() {
    this.checkProfile();
    // Загружаем команды пользователя только если они есть в профиле
    if (this.profile && Array.isArray(this.profile.teams) && this.profile.teams.length > 0) {
      this.getParticipantTeams();
    }
    window.addEventListener('hashchange', this.onHashChange);
    this.onHashChange();

    // Добавляем слушатель изменения размера окна для проверки табов
    window.addEventListener('resize', this.checkTabsOverflow);

    // Проверяем табы после загрузки страницы
    this.$nextTick(() => {
      setTimeout(() => {
        this.checkTabsOverflow();
      }, 100);
    });
  },
  beforeDestroy() {
    window.removeEventListener('hashchange', this.onHashChange);
    window.removeEventListener('resize', this.checkTabsOverflow);
  },
};
</script>

<style lang="scss" scoped>
    @import "./Team";
</style>
